import { ChevronDown, ChevronUp, FileX } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { buildHighlightCandidates } from '../../lib/markdownPlainText';
import { cn } from '../../lib/utils';
import { EmptyState } from '../emptyState';
import { LoadingState } from '../loadingState';
import { Caption } from '../typography';
import { VButton } from '../vButton';
import { VInput } from '../vInput';

import { useDocxProcessor } from './useDocxProcessor';
import { useDocxSearch } from './useDocxSearch';

type VDocxViewerProps = {
  /** Het .docx-document; wordt exact één keer als ArrayBuffer ingelezen. */
  source: Blob | ArrayBuffer;
  /**
   * Extern gestuurde highlight (bv. een geselecteerde evidence-zin).
   * `plainSentence` is de door de backend berekende Markdown-vrije variant;
   * ontbreekt die (bestaande data), dan strippen we `sentence` client-side.
   */
  highlightTarget?: { sentence?: string; plainSentence?: string } | null;
  onReady?: () => void;
  onError?: (error: Error) => void;
  className?: string;
};

/**
 * Hybride DOCX-viewer: docx-preview als zichtbare, visueel getrouwe laag;
 * mammoth als semantische achtergrondlaag (zoekindex + verrijkings-HTML);
 * eigen interactielaag voor full-text search, highlight en scroll.
 */
export function VDocxViewer({
  source,
  highlightTarget,
  onReady,
  onError,
  className,
}: VDocxViewerProps) {
  // Callback-ref via state: het processor-effect moet draaien zodra het element bestaat.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { status, semantic, error } = useDocxProcessor(source, container);
  const [query, setQuery] = useState('');

  // Een getypte zoekterm hoort bij het document waarin hij is ingevoerd — bij
  // een nieuw document (nieuwe source) resetten, anders overschrijft een
  // stale query stilzwijgend de evidence-highlight van het volgende document.
  useEffect(() => {
    setQuery('');
  }, [source]);

  // Kandidaten voor het externe evidence-target: eerst de backend-platte
  // tekst, dan de client-side gestripte sentence als terugval (bestaande data
  // of geen match op de backend-tekst).
  const evidenceCandidates = useMemo(
    () =>
      highlightTarget
        ? buildHighlightCandidates(highlightTarget.sentence, highlightTarget.plainSentence)
        : [],
    [highlightTarget],
  );

  // Twee triggers, één mechanisme: zoekbalk-input wint van het externe evidence-target.
  const terms = query.trim() ? [query.trim()] : evidenceCandidates;
  const hasTerm = terms.length > 0;

  const { matchCount, activeIndex, goNext, goPrev } = useDocxSearch({
    container,
    ready: status === 'ready',
    terms,
    rawText: semantic?.rawText ?? null,
  });

  // Callbacks in refs zodat statuswissels geen effect-churn door nieuwe closures krijgen.
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  onReadyRef.current = onReady;
  onErrorRef.current = onError;

  useEffect(() => {
    if (status === 'ready') onReadyRef.current?.();
  }, [status]);

  useEffect(() => {
    if (status === 'error' && error) onErrorRef.current?.(error);
  }, [status, error]);

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-background px-3 py-2">
        <VInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoeken in document..."
          className="h-8 flex-1"
          disabled={status !== 'ready'}
        />
        <Caption tone="muted" className="whitespace-nowrap">
          {hasTerm && matchCount > 0 && `${activeIndex + 1} van ${matchCount}`}
          {hasTerm && matchCount === 0 && '0 resultaten'}
        </Caption>
        <VButton
          variant="ghost"
          size="sm"
          onClick={goPrev}
          disabled={matchCount === 0}
          aria-label="Vorig resultaat"
        >
          <ChevronUp className="size-4" />
        </VButton>
        <VButton
          variant="ghost"
          size="sm"
          onClick={goNext}
          disabled={matchCount === 0}
          aria-label="Volgend resultaat"
        >
          <ChevronDown className="size-4" />
        </VButton>
      </div>
      <div className="flex-1 overflow-y-auto bg-muted/30 p-4">
        {(status === 'idle' || status === 'processing') && (
          <LoadingState message="Word-document laden..." />
        )}
        {status === 'error' && (
          <EmptyState
            icon={FileX}
            title="Kon Word-document niet weergeven"
            description="Het bestand lijkt beschadigd of gebruikt niet-ondersteunde functies."
          />
        )}
        {/* Container blijft gemount: docx-preview heeft een levend element nodig
            tijdens het renderen; bij een error verbergen we het restant. */}
        <div ref={setContainer} className={cn('mx-auto', status === 'error' && 'hidden')} />
      </div>
    </div>
  );
}

export type { VDocxViewerProps };
